"use client";

import {CSSProperties, JSX, ReactNode, useEffect, useState} from "react";

import {createPortal} from "react-dom";

import {useLockedBody} from "@/shared/hooks";

import "./styles/modal.scss";

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    header?: ReactNode;
    footer?: ReactNode;
    className?: string;
    contentClassName?: string;
    ariaLabel?: string;
    closeOnBackdropClick?: boolean;
    buttonCloseType?: "primary" | "outside";
    /**
     * If true, locks background scroll when modal is open.
     * @default false
     */
    lockScroll?: boolean;
    /**
     * If true, renders modal via React portal into document.body,
     * bypassing any parent stacking context (e.g. sticky headers).
     * @default false
     */
    usePortal?: boolean;
    /** Override padding of the modal body */
    bodyPadding?: CSSProperties["padding"];
    /** Override border-radius of the modal content */
    borderRadius?: CSSProperties["borderRadius"];
}

export function Modal({
    isOpen,
    onClose,
    children,
    header,
    footer,
    className = "",
    contentClassName = "",
    ariaLabel = "Dialog window",
    closeOnBackdropClick = true,
    buttonCloseType = "primary",
    lockScroll = false,
    usePortal = false,
    bodyPadding,
    borderRadius,
}: ModalProps): JSX.Element | null {
    const [isClosing, setIsClosing] = useState(false);
    const [shouldRender, setShouldRender] = useState(isOpen);

    useLockedBody({locked: lockScroll && isOpen});

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsClosing(false);
        } else if (shouldRender) {
            setIsClosing(true);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setIsClosing(false);
            }, 300); // Match animation duration

            return () => {
                clearTimeout(timer);
            };
        }
    }, [isOpen, shouldRender]);

    const handleClose = (): void => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, 300); // Match animation duration
    };

    if (!shouldRender) return null;

    const content = (
        <div className={`modal ${isClosing ? "modal--closing" : ""} ${className}`} role="dialog" aria-modal="true" aria-label={ariaLabel}>
            <div className="modal__backdrop" onClick={closeOnBackdropClick ? handleClose : undefined} />
            <div className={`modal__content ${contentClassName}`} style={borderRadius !== undefined ? {borderRadius} : undefined}>
                {buttonCloseType === "outside" && (
                    <button type="button" className="modal__close modal__close--outside" aria-label="Close" onClick={handleClose}>
                        ✕
                    </button>
                )}
                {buttonCloseType === "primary" && (
                    <button type="button" className="modal__close modal__close--primary" aria-label="Close" onClick={handleClose}>
                        ✕
                    </button>
                )}
                {header && <div className="modal__header">{header}</div>}
                <div className="modal__body" style={bodyPadding !== undefined ? {padding: bodyPadding} : undefined}>
                    {children}
                </div>
                {footer && <div className="modal__footer">{footer}</div>}
            </div>
        </div>
    );

    if (usePortal) {
        return createPortal(content, document.body);
    }

    return content;
}
